import { Avatar, type AvatarProps } from '@mui/material';

type UserAvatarProps = AvatarProps & {
  readonly displayName?: string | null;
  readonly photoURL?: string | null;
  readonly email?: string | null;
};

/**
 * Generate initials from display name or email
 */
const getInitials = (displayName?: string | null, email?: string | null): string => {
  if (displayName && displayName.trim()) {
    const parts = displayName.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      const first = parts[0];
      const last = parts[parts.length - 1];
      if (first && last) {
        // First + Last initial
        return `${first[0]}${last[0]}`.toUpperCase();
      }
    }
    // Single name - first 2 chars or just first char
    return displayName.substring(0, Math.min(2, displayName.length)).toUpperCase();
  }

  if (email && email.trim()) {
    // Use first 2 chars of email username
    const parts = email.split('@');
    const username = parts[0];
    if (username && username.length > 0) {
      return username.substring(0, Math.min(2, username.length)).toUpperCase();
    }
  }

  return 'M'; // Default to "M" for Member
};

/**
 * Generate consistent color from string (for avatar background)
 */
const stringToColor = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  const hue = hash % 360;
  return `hsl(${hue}, 60%, 50%)`;
};

/**
 * User avatar component with automatic initials fallback
 * Shows photo if available, otherwise displays initials with colored background
 */
const UserAvatar = ({ displayName, photoURL, email, sx, ...props }: UserAvatarProps) => {
  const initials = getInitials(displayName, email);
  const backgroundColor = stringToColor(displayName || email || 'default');

  if (photoURL) {
    return (
      <Avatar src={photoURL} alt={displayName || 'User'} sx={sx} {...props}>
        {initials}
      </Avatar>
    );
  }

  return (
    <Avatar
      sx={{
        bgcolor: backgroundColor,
        fontWeight: 600,
        ...sx,
      }}
      {...props}
    >
      {initials}
    </Avatar>
  );
};

export default UserAvatar;
